import {api, isPaginatedQuery} from '@services/Api'
import {transformIssueResponse} from "@mwServices/Issue";
import {DEFAULT_POSTS_PER_PAGE} from "@lib/constants";
import {removeEncodedChars} from "@lib/format";

interface UserResponse {
  user: {
    user_nickname: string;
    user_email: string;
    user_registered: string;
    display_name: string;
    type: string;
    permissions: {
      rules: string[];
      [permission:string]: boolean | string[];
    }
  } | null;
}

interface IItemValue {
  id: number;
  item_id: number;
  purchase_price: number;
  graded_by: string;
  grade: string;
  estval: number;
  comic_url: string;
  comic_title: string;
}

interface ICollectionOverview {
  user_id: number;
  info: {
    items: number;
    total_spend: number;
    value: number;
    top_value_item?: IItemValue,
    top_value_raw?: IItemValue,
    top_value_graded?: IItemValue,
    items_hot: number;
    items_key: number;
    items_rare: number;
    ungraded: number;
    graded: number;
    items_wishlist: number;
    top_box: null | {id :number, name: string, fmv: number, fmv_updated: string}
    fmv: number | null;
    fmv_updated?: string | null;
    series_count?: number | null;
    box_count?: number | null;
    fmv_can_update?: boolean | null;
    fmv_is_stale?: boolean | null;
    calculation_processing?: boolean | null;
  }
}

interface IFilterOptions {
  series?: number[],
  boxes?: number[],
  is_wishlist?: boolean;
}

interface ICollectionFilterOptions {
  locations: {location,count}[];
  grades: {grade,count}[];
  gradedBy: {graded_by,count}[];
  publishers: {publisher_id,publisher,total}[];
  valuesShown: {covrprice, user};
  eras: {id,name,start_date,end_date,count}[];
  seriesFormats: {format}[];
  getItSlabbed: number;
}

export const transformMostValuableResponse = (mostValuable) => {
  return mostValuable ?  {
    ...mostValuable,
    comic_title: removeEncodedChars(mostValuable.comic_title),
    stale: (String(mostValuable.stale) === '1'),
  } : undefined;
};

const transformOverviewResponse = (responseData: ICollectionOverview) => {
  return {
    ...responseData,
    info: {
      ...responseData.info,
      items: Number(responseData.info.items),
      total_spend: Number(responseData.info.total_spend),
      value: Number(responseData.info.value),
      items_hot: Number(responseData.info.items_hot),
      items_key: Number(responseData.info.items_key),
      items_rare: Number(responseData.info.items_rare),
      ungraded: Number(responseData.info.ungraded),
      graded: Number(responseData.info.graded),
      items_wishlist: Number(responseData.info.items_wishlist),
      fmv: Number(responseData.info.fmv),
      series_count: Number(responseData.info.series_count),
      box_count: Number(responseData.info.box_count),
      fmv_can_update: (String(responseData.info.fmv_can_update) === '1'),
      fmv_is_stale: (String(responseData.info.fmv_is_stale) === '1'),
      calculation_processing: (String(responseData.info.calculation_processing) === '1'),
      top_value_raw: transformMostValuableResponse(responseData.info.top_value_raw),
      top_value_graded: transformMostValuableResponse(responseData.info.top_value_graded),
    }
  };
};

const userApi = api.injectEndpoints({
  endpoints: (build) => ({
    wishlistAddIssue: build.mutation({
      query: (issueIds) => {
        const payload = {
          'issue_ids': `${issueIds}`,
        };
        return {
          url: `covr/v1/user/wishlist/add`,
          method: "POST",
          body: payload,
        };
      },
      invalidatesTags: ['Issue','Overview'],
    }),
    wishlistRemoveIssue: build.mutation({
      query: (issueIds) => {
        const payload = {
          'issue_ids': `${issueIds}`,
        };
        return {
          url: `covr/v1/user/wishlist/remove`,
          method: "POST",
          body: payload,
        };
      },
      invalidatesTags: ['Issue','Overview'],
    }),
    wishlistUpdateIssue: build.mutation({
      query: (payload) => {
        return {
          url: `covr/v1/user/wishlist/update`,
          method: "POST",
          body: payload,
        };
      },
      invalidatesTags: ['Issue','Overview'],
    }),
    collectionAddIssue: build.mutation({
      query: (payload) => {
        return {
          url: `covr/v1/user/collection/add`,
          method: "POST",
          body: payload,
        };
      },
      invalidatesTags: ['Issue','Overview','Boxes','Series'],
    }),
    collectionAddIssues: build.mutation({
      query: (payload) => {
        return {
          url: `covr/v1/user/collection/add/multiple`,
          method: "POST",
          body: payload,
        };
      },
      invalidatesTags: ['Issue','Overview','Boxes','Series'],
    }),
    collectionAddIssueWishlistRemoveIssue: build.mutation({
      query: (payload) => {
        return {
          url: `covr/v1/user/collection/add/wishlist/remove`,
          method: "POST",
          body: payload,
        };
      },
      invalidatesTags: ['Issue','Overview','Boxes','Series'],
    }),
    collectionAddIssuesWishlistRemoveIssue: build.mutation({
      query: (payload) => {
        return {
          url: `covr/v1/user/collection/add/multiple/wishlist/remove`,
          method: "POST",
          body: payload,
        };
      },
      invalidatesTags: ['Issue','Overview','Boxes','Series'],
    }),
    collectionUpdateIssue: build.mutation({
      query: (payload) => {
        return {
          url: `covr/v1/user/collection/update`,
          method: "POST",
          body: payload,
        };
      },
      invalidatesTags: ['Issue','Overview','Boxes','Series'],
    }),
    collectionAssignCondition: build.mutation({
      query: (payload) => {
        return {
          url: `covr/v1/user/collection/assign_condition`,
          method: "POST",
          body: payload,
        };
      },
      invalidatesTags: ['Issue','Overview','Boxes','Series'],
    }),
    seriesAssignCondition: build.mutation({
      query: (payload) => {
        return {
          url: `covr/v1/user/series/assign_condition`,
          method: "POST",
          body: payload,
        };
      },
      invalidatesTags: ['Issue','Overview','Series','Boxes'],
    }),
    collectionAssignLocation: build.mutation({
      query: (payload) => {
        return {
          url: `covr/v1/user/collection/assign_location`,
          method: "POST",
          body: payload,
        };
      },
      invalidatesTags: ['Issue','Overview'],
    }),
    seriesAssignLocation: build.mutation({
      query: (payload) => {
        return {
          url: `covr/v1/user/series/assign_location`,
          method: "POST",
          body: payload,
        };
      },
      invalidatesTags: ['Issue','Overview','Series'],
    }),
    collectionAssignPurchasePrice: build.mutation({
      query: (payload) => {
        return {
          url: `covr/v1/user/collection/assign_purchase_price`,
          method: "POST",
          body: payload,
        };
      },
      invalidatesTags: ['Issue','Overview'],
    }),
    collectionRemoveIssue: build.mutation({
      query: (payload) => {
        return {
          url: `covr/v1/user/collection/remove`,
          method: "POST",
          body: payload,
        };
      },
      invalidatesTags: ['Issue','Overview','Boxes','Series'],
    }),
    collectionRemoveIssues: build.mutation({
      query: (payload) => {
        return {
          url: `covr/v1/user/collection/remove_issues`,
          method: "POST",
          body: payload,
        };
      },
      invalidatesTags: ['Issue','Overview','Boxes','Series'],
    }),
    collectionRemoveSeries: build.mutation({
      query: (payload) => {
        return {
          url: `covr/v1/user/collection/remove_series`,
          method: "POST",
          body: payload,
        };
      },
      invalidatesTags: ['Issue','Overview','Series','Boxes'],
    }),
    collectionCloneIssue: build.mutation({
      query: (payload) => {
        return {
          url: `covr/v1/user/collection/clone`,
          method: "POST",
          body: payload,
        };
      },
      invalidatesTags: ['Issue','Overview','Boxes','Series'],
    }),
    getCollectionOverview: build.query<ICollectionOverview, void>({
      query: () => {
        return {
          url: `covr/v1/my-collection/stats`,
          method: "GET",
        };
      },
      providesTags: ['Overview'],
      transformResponse: transformOverviewResponse,
    }),
    getCollectionFilterOptions: build.query<ICollectionFilterOptions,IFilterOptions>({
      query: (args) => {
        const filters = args ? encodeURIComponent(btoa(JSON.stringify(args))) : '';
        return {
          url: `covr/v1/my-collection/filter-options?filters=${filters}`,
          method: "GET",
        };
      },
      providesTags: ['Overview'],
    }),
    getCollectionLocations: build.query<{location,count}[],IFilterOptions>({
      query: (args) => {
        const filters = args ? encodeURIComponent(btoa(JSON.stringify(args))) : '';
        return {
          url: `covr/v1/my-collection/locations?filters=${filters}`,
          method: "GET",
        };
      },
      providesTags: ['Overview'],
    }),
    getCollectionGrades: build.query<{grade,count}[],IFilterOptions>({
      query: (args) => {
        const filters = args ? encodeURIComponent(btoa(JSON.stringify(args))) : '';
        return {
          url: `covr/v1/my-collection/grades?filters=${filters}`,
          method: "GET",
        };
      },
      providesTags: ['Overview'],
    }),
    getCollectionGradedBy: build.query<{graded_by,count}[],IFilterOptions>({
      query: (args) => {
        const filters = args ? encodeURIComponent(btoa(JSON.stringify(args))) : '';
        return {
          url: `covr/v1/my-collection/graded_by?filters=${filters}`,
          method: "GET",
        };
      },
      providesTags: ['Overview'],
    }),
    getCollectionPublishers: build.query<{publisher_id,publisher,total}[],IFilterOptions>({
      query: (args) => {
        const filters = args ? encodeURIComponent(btoa(JSON.stringify(args))) : '';
        return {
          url: `covr/v1/my-collection/publishers?filters=${filters}`,
          method: "GET",
        };
      },
      providesTags: ['Overview'],
    }),
    getCollectionValuesShown: build.query<{covrprice, user},IFilterOptions>({
      query: (args) => {
        const filters = args ? encodeURIComponent(btoa(JSON.stringify(args))) : '';
        return {
          url: `covr/v1/my-collection/values_shown?filters=${filters}`,
          method: "GET",
        };
      },
      providesTags: ['Overview'],
    }),
    getCollectionEras: build.query<{id,name,start_date,end_date,count}[],IFilterOptions>({
      query: (args) => {
        const filters = args ? encodeURIComponent(btoa(JSON.stringify(args))) : '';
        return {
          url: `covr/v1/my-collection/eras?filters=${filters}`,
          method: "GET",
        };
      },
      providesTags: ['Overview'],
    }),
    getCollectionSeriesFormats: build.query<any,IFilterOptions>({
      query: (args) => {
        const filters = args ? encodeURIComponent(btoa(JSON.stringify(args))) : '';
        return {
          url: `covr/v1/my-collection/series_formats?filters=${filters}`,
          method: "GET",
        };
      },
      providesTags: ['Overview'],
    }),
    getCollectionIssues: build.query({
      query: (args) => {
        const filters = args ? encodeURIComponent(btoa(JSON.stringify(args.filters))) : '';

        if(isPaginatedQuery(args)) {
          const page = args ? args.page : 1;
          const perPage = args ? args.perPage : DEFAULT_POSTS_PER_PAGE;
          return {
            url: `covr/v1/my-collection/issues?filters=${filters}&page=${page}&per_page=${perPage}`,
            method: "GET",
          };
        }
        return {
          url: `covr/v1/my-collection/issues?filters=${filters}`,
          method: "GET",
        };
      },
      providesTags: ['Issue'],
      transformResponse: transformIssueResponse,
    }),
    getWishlistSeries: build.query<any,void>({
      query: () => {
        return {
          url: `covr/v1/user/wishlist/series`,
          method: "GET",
        };
      },
      providesTags: ['Overview'],
    }),
    getWishlistIssues: build.query({
      query: (args) => {
        const filters = args ? encodeURIComponent(btoa(JSON.stringify(args.filters))) : '';

        if(isPaginatedQuery(args)) {
          const page = args ? args.page : 1;
          const perPage = args ? args.perPage : DEFAULT_POSTS_PER_PAGE;
          return {
            url: `covr/v1/user/wishlist/issues?filters=${filters}&page=${page}&per_page=${perPage}`,
            method: "GET",
          };
        }
        return {
          url: `covr/v1/user/wishlist/issues?filters=${filters}`,
          method: "GET",
        };
      },
      providesTags: ['Issue'],
      transformResponse: transformIssueResponse,
    }),
    getLoggedInUser: build.query<UserResponse, void>({
      query: () => {
        return {
          url: `covr/v1/user/logged_in`,
          method: "GET",
        };
      },
      providesTags: ['Account'],
    }),
    collectionMarkAsSold: build.mutation({
      query: (payload) => {
        return {
          url: `covr/v1/user/collection/mark_as_sold`,
          method: "POST",
          body: payload,
        };
      },
      invalidatesTags: ['Issue','Overview','Boxes','Series'],
    }),
    getTransactionVenues: build.query<{venue}[],void>({
      query: () => {
        return {
          url: `covr/v1/my-collection/venues`,
          method: "GET",
        };
      },
      providesTags: ['Overview'],
    }),
    getShippingCompanies: build.query<{shipping_co}[],void>({
      query: () => {
        return {
          url: `covr/v1/my-collection/shipping_companies`,
          method: "GET",
        };
      },
      providesTags: ['Overview'],
    }),
    saveContact: build.mutation({
      query: (payload) => {
        return {
          url: `covr/v1/user/contacts/save`,
          method: "POST",
          body: payload,
        };
      },
      invalidatesTags: ['Contact'],
    }),
    getContacts: build.query<any[],void>({
      query: () => {
        return {
          url: `covr/v1/user/contacts`,
          method: "GET",
        };
      },
      providesTags: ['Contact'],
    }),
    getSignedByIds: build.query<any[],any>({
      query: (args) => {
        const ids = args.ids;
        return {
          url: `covr/v1/user/signed_by_ids?ids=${ids}`,
          method: "GET",
        };
      },
    }),
    refreshCollectionFmv: build.mutation({
      query: () => {
        return {
          url: `covr/v1/user/collection/refresh_value`,
          method: "POST",
          body: {},
        };
      },
      invalidatesTags: ['Overview'],
    }),
    wishlistAddIssuesFromFilters: build.mutation({
      query: (payload) => {
        return {
          url: `covr/v1/user/wishlist/add_by_filters`,
          method: "POST",
          body: payload,
        };
      },
      invalidatesTags: ['Issue','Overview'],
    }),
    collectionAddIssuesByFilters: build.mutation({
      query: (payload) => {
        return {
          url: `covr/v1/user/collection/add_by_filters`,
          method: "POST",
          body: payload,
        };
      },
      invalidatesTags: ['Issue','Overview','Boxes','Series'],
    }),
  })
});

export const {
  useWishlistAddIssueMutation,
  useWishlistRemoveIssueMutation,
  useCollectionAddIssueWishlistRemoveIssueMutation,
  useCollectionAddIssuesWishlistRemoveIssueMutation,
  useWishlistUpdateIssueMutation,
  useCollectionAddIssueMutation,
  useCollectionAddIssuesMutation,
  useCollectionRemoveIssueMutation,
  useCollectionRemoveIssuesMutation,
  useCollectionCloneIssueMutation,
  useGetLoggedInUserQuery,
  useGetCollectionOverviewQuery,
  useGetCollectionIssuesQuery,
  useGetCollectionFilterOptionsQuery,
  useGetCollectionLocationsQuery,
  useGetCollectionGradesQuery,
  useGetCollectionGradedByQuery,
  useGetCollectionPublishersQuery,
  useGetCollectionValuesShownQuery,
  useGetCollectionErasQuery,
  useCollectionUpdateIssueMutation,
  useGetCollectionSeriesFormatsQuery,
  useCollectionAssignConditionMutation,
  useCollectionAssignLocationMutation,
  useCollectionAssignPurchasePriceMutation,
  useCollectionMarkAsSoldMutation,
  useGetTransactionVenuesQuery,
  useGetShippingCompaniesQuery,
  useSaveContactMutation,
  useGetContactsQuery,
  useGetSignedByIdsQuery,
  useGetWishlistIssuesQuery,
  useCollectionRemoveSeriesMutation,
  useSeriesAssignConditionMutation,
  useSeriesAssignLocationMutation,
  useGetWishlistSeriesQuery,
  useRefreshCollectionFmvMutation,
  useWishlistAddIssuesFromFiltersMutation,
  useCollectionAddIssuesByFiltersMutation,
} = userApi;
